/**
 * Phone step of the Connect Splyt page. Same invisible reCAPTCHA pattern and
 * error copy as JoinPhoneContainer; US numbers only.
 */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { sendCode, createRecaptchaVerifier } from '../join/firebaseAuth';
import { track, EVENTS, SCREEN } from '../../analytics/track';
import useConnectStyles from './connectStyles';
import ConnectShell, { useIsDesktop } from './ConnectShell';
import { assistantName, formatUsPhone, toUsE164 } from './connectHelpers';

const RECAPTCHA_CONTAINER_ID = 'connect-recaptcha-container';

export default function ConnectPhoneStep({ clientName, onCodeSent }) {
  const classes = useConnectStyles();
  const isDesktop = useIsDesktop();
  const inputRef = useRef(null);

  const [phone, setPhone] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  useEffect(function trackPhoneAuthScreen() {
    track(EVENTS.SCREEN_VIEWED, { screen_name: SCREEN.PHONE_AUTH });
  }, []);

  useEffect(() => {
    const verifier = createRecaptchaVerifier(RECAPTCHA_CONTAINER_ID);
    setRecaptchaVerifier(verifier);
    return () => {
      try {
        verifier.clear();
      } catch (err) {
        // Already cleared by Firebase; nothing to release.
      }
    };
  }, []);

  const isValidPhone = phone.length === 10;

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
    if (error) setError('');
  };

  const handleSubmit = async () => {
    if (!isValidPhone || isLoading) return;
    const e164Phone = toUsE164(phone);
    if (!e164Phone) {
      setError("That phone number doesn't look right.");
      return;
    }

    setIsLoading(true);
    setError('');
    // Close the keyboard first: a visible reCAPTCHA challenge is centered on
    // the layout viewport and would sit behind an open keyboard.
    if (inputRef.current) inputRef.current.blur();

    try {
      await sendCode(e164Phone, recaptchaVerifier, { displayPhone: formatUsPhone(phone) });
      onCodeSent();
    } catch (err) {
      console.error('[connect] send code failed:', err);
      setError(err.message || 'Failed to send code. Please try again.');
      setIsLoading(false);
    }
  };

  const content = (
    <>
      <h2 className={`${classes.title} ${isDesktop ? classes.titleDesktop : classes.titleMobile}`}>
        Sign in to Splyt
      </h2>
      <p className={classes.subtitle}>
        {assistantName(clientName)} asked to connect to your Splyt account. We text a code to
        your number to confirm it&apos;s you.
      </p>

      <div className={classes.phoneInputRow}>
        <div className={classes.countryPicker} aria-label="United States, +1">
          <span className={classes.countryFlag} aria-hidden="true">🇺🇸</span>
          <span>+1</span>
        </div>
        <div
          className={`${classes.inputWrapper} ${classes.phoneInputWrapper} ${
            isFocused ? classes.inputWrapperFocused : classes.inputWrapperDefault
          }`}
        >
          {/* enterkeyhint is lowercase on purpose: React 16 passes unknown
              lowercase attributes straight through but warns on camelCase ones. */}
          <input
            ref={inputRef}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            // eslint-disable-next-line react/no-unknown-property
            enterkeyhint="go"
            className={`${classes.textInput} ${classes.tabularInput}`}
            placeholder="(555) 555-5555"
            value={formatUsPhone(phone)}
            onChange={handlePhoneChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={isLoading}
            aria-label="Phone number"
          />
        </div>
      </div>

      <div className={classes.privacyNote}>
        <span className={classes.privacyEmoji} aria-hidden="true">🔒</span>
        <div className={classes.privacyText}>
          Your number is only used to verify it&apos;s you and notify you of new
          splyts. We won&apos;t text spam, ever.
        </div>
      </div>

      {error && <div className={classes.errorMessage} role="alert">{error}</div>}
    </>
  );

  const footer = (
    <>
      <button
        type="button"
        className={classes.primaryButton}
        disabled={!isValidPhone || isLoading}
        onClick={handleSubmit}
        // iOS fires blur before click: without this the first tap on the pinned
        // bar only closes the keyboard and the button slides out from under the finger.
        onMouseDown={(e) => e.preventDefault()}
      >
        {isLoading ? 'Sending...' : 'Text me a code'}
      </button>
      <p className={classes.caption}>
        By tapping Text me a code, you agree to our{' '}
        <a
          className={classes.captionLink}
          href="https://www.splyt.co/terms"
          rel="noreferrer"
          target="_blank"
        >
          Terms
        </a>
        {' '}&amp;{' '}
        <a
          className={classes.captionLink}
          href="https://www.splyt.co/privacy"
          rel="noreferrer"
          target="_blank"
        >
          Privacy Policy
        </a>
        {' '}and consent to receive text messages from us on behalf of organizers.
        Messages and data rates apply. Text{' '}
        <span className={classes.captionUpper}>stop</span>
        {' '}to cancel.
      </p>
    </>
  );

  return (
    <>
      <ConnectShell footer={footer}>{content}</ConnectShell>
      <span id={RECAPTCHA_CONTAINER_ID} />
    </>
  );
}

ConnectPhoneStep.propTypes = {
  clientName: PropTypes.string,
  onCodeSent: PropTypes.func.isRequired,
};

ConnectPhoneStep.defaultProps = {
  clientName: '',
};
